import { trpc } from "../trpc";
import { SearchTablesOutput } from "@zapier/bff";

export type SearchTablesCache = SearchTablesOutput | undefined;

function addTableToSearchResults(
  cache: SearchTablesCache,
  newTable: NonNullable<SearchTablesOutput>["data"][0],
): SearchTablesCache {
  if (!cache) {
    return cache;
  }

  return {
    ...cache,
    data: [newTable, ...cache.data],
  };
}

interface Input {
  folderId?: string;
}

export function useDuplicateTable(input?: Input) {
  const { folderId } = input || {};
  const utils = trpc.useUtils();

  return trpc.table.duplicate.useMutation({
    onMutate: async (variables) => {
      // Cancel outgoing refetches
      await utils.table.search.cancel();

      // Snapshot the previous value
      const previousTables = utils.table.search.getData({});

      // Don't do optimistic update if we're in a folder view (folderId present)
      // because the table won't be in the search results until after the move
      if (!folderId && previousTables?.data && variables.sourceTableId) {
        // Find the source table to use as template
        const sourceTable = previousTables.data.find(
          (t) => t.id === variables.sourceTableId,
        );

        if (sourceTable) {
          // Create optimistic table with the data we know
          const optimisticTable = {
            ...sourceTable,
            id: `temp-${Date.now()}`, // Temporary ID
            name: variables.name || sourceTable.name,
            description: variables.description || sourceTable.description,
            createdAt: new Date().toISOString(),
            editedAt: new Date().toISOString(),
          };

          // Optimistically update the cache
          utils.table.search.setData({}, (cache: SearchTablesCache) =>
            addTableToSearchResults(cache, optimisticTable),
          );
        }
      }

      return { previousTables };
    },
    onError: (_error, _variables, context) => {
      // Rollback to previous value on error
      if (context?.previousTables) {
        utils.table.search.setData({}, context.previousTables);
      }
    },
    onSuccess: (response) => {
      // Replace optimistic data with real data
      if (response?.data) {
        utils.table.search.setData({}, (cache: SearchTablesCache) => {
          if (!cache) return cache;

          // Remove any temp entries and ensure the real table is at the top
          const filteredData = cache.data.filter(
            (t) => !t.id.startsWith("temp-"),
          );

          return {
            ...cache,
            data: [response.data, ...filteredData],
          };
        });
      }
    },
    onSettled: () => {
      utils.table.search.invalidate();
      if (folderId) {
        utils.asset.getHydratedFolderAssets.invalidate({
          folderId,
        });
      }
    },
  });
}
